// FILE: src/components/QuestionCard.js
// Displays a single question and its answer choices.
// Supports both single-select and multi-select questions.
//
// Props:
//   question              {object}   — normalized question object
//   selectedAnswers       {string[]} — currently selected choice IDs
//   onSelectAnswer        {function} — called with new selectedAnswers array
//   onSubmit              {function} — called when "Submit Answer" is clicked
//   onPrevious            {function} — (optional) called when "← Previous" is clicked
//   isPreviousDisabled    {boolean}  — disables "← Previous" when on the first question
//   onSkip                {function} — (optional) called when "Skip →" is clicked
//   isSkipped             {boolean}  — shows "Previously skipped" badge when true
//   isSubmitted           {boolean}  — disables the Skip button once answer is submitted
//   eliminatedChoices     {string[]} — (optional) choice ids the learner has struck out
//   onToggleElimination   {function} — (optional) called with choiceId to toggle elimination
//
// ── Elimination feature ───────────────────────────────────────────────────────
//
//   Each choice has a small "⊘" button on the right that toggles a visual
//   strikethrough on the choice text.  Elimination is completely independent
//   from selection — selecting or submitting a choice does not clear its
//   eliminated state and vice-versa.  The eliminate button is hidden once the
//   question is submitted (isSubmitted=true) to reduce visual noise.
//
//   State is owned by App.js and persisted per question index in questionStates
//   snapshots so navigation (Previous / Skip) correctly restores eliminations.
//
// ── Flag feature ──────────────────────────────────────────────────────────────
//
//   A 🚩 Flag button appears in the action row between ← Previous and ⚑ Report.
//   Flagging is independent of the answer — flagging / unflagging never clears
//   a selection or submission.  Flagged questions are highlighted in the
//   QuestionNavigator panel so the learner can easily find and revisit them.

import React, { useEffect } from 'react';
import { getQuestionType } from '../../questionTypes';
import QuestionExhibit from '../questions/QuestionExhibit';
import css from './QuestionCard.module.css';

const styles = {
  card: {
    maxWidth: '720px',
    margin: '0 auto',
    padding: '32px',
    border: '2px solid #333',
    borderRadius: '8px',
    background: '#fff',
  },
  meta: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.8rem',
    color: '#888',
    marginBottom: '16px',
  },
  skippedBadge: {
    display: 'inline-block',
    padding: '3px 10px',
    background: '#fff3e0',
    color: '#e07b00',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: '600',
    marginBottom: '10px',
  },
  questionText: {
    fontSize: '1.1rem',
    lineHeight: '1.6',
    marginBottom: '24px',
    fontWeight: '500',
  },
  multiSelectNote: {
    fontSize: '0.85rem',
    color: '#e07b00',
    marginBottom: '12px',
    fontStyle: 'italic',
  },
  choicesList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  // Wrapper that places the choice button and the eliminate toggle side-by-side.
  choiceRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '8px',
  },
  choiceButton: (isSelected, isEliminated) => ({
    flex: 1,
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '1rem',
    cursor: 'pointer',
    borderRadius: '6px',
    border: isSelected ? '2px solid #333' : '2px solid #ccc',
    background: isSelected ? '#f0f0f0' : '#fff',
    fontWeight: isSelected ? '600' : 'normal',
    opacity: isEliminated ? 0.45 : 1,
    textDecoration: isEliminated ? 'line-through' : 'none',
    transition: 'border 0.1s, background 0.1s, opacity 0.1s',
  }),
  choiceId: {
    fontWeight: 'bold',
    marginRight: '10px',
  },
  // The small eliminate / restore toggle button.  Shown only before submission.
  eliminateButton: (isEliminated) => ({
    flexShrink: 0,
    padding: '0 10px',
    fontSize: '1.1rem',
    lineHeight: 1,
    cursor: 'pointer',
    background: isEliminated ? '#fce8e8' : 'transparent',
    color: isEliminated ? '#c62828' : '#bbb',
    border: `1px solid ${isEliminated ? '#e88' : '#ddd'}`,
    borderRadius: '6px',
    transition: 'color 0.1s, background 0.1s, border 0.1s',
  }),
  // 🚩 Flag button — toggles the review flag for this question.
  // Placed in the action row between ← Previous and ⚑ Report.
  flagButton: {
    padding: '12px 16px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    background: 'transparent',
    color: '#aaa',
    border: '1px solid #e8e8e8',
    borderRadius: '6px',
    transition: 'color 0.12s, border-color 0.12s, background 0.12s',
    whiteSpace: 'nowrap',
  },
  flagButtonActive: {
    color: '#1565c0',
    border: '1px solid #1565c0',
    background: '#e8f0fe',
  },

  // Small, unobtrusive "⚑ Report" button shown at the left of the action row.
  // Placed between Previous and the right-side group so it is discoverable but
  // does not compete visually with the primary Submit action.
  reportButton: {
    padding: '12px 16px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    background: 'transparent',
    color: '#aaa',
    border: '1px solid #e8e8e8',
    borderRadius: '6px',
    transition: 'color 0.12s, border-color 0.12s',
    whiteSpace: 'nowrap',
  },

  // ── Submit row ──────────────────────────────────────────────────────────────
  //
  // Layout:  [← Previous] [⚑ Report]    [Skip →]  [Submit Answer]
  //           far-left                   right group (marginLeft:auto)
  //
  // "← Previous" is only rendered when onPrevious is provided.
  // "Skip →"     is only rendered when onSkip     is provided.
  // The rightActions wrapper always carries marginLeft:'auto' so Submit
  // stays pinned to the right regardless of which left-side buttons appear.
  submitRow: {
    marginTop: '28px',
    display: 'flex',
    alignItems: 'center',
  },
  rightActions: {
    display: 'flex',
    gap: '12px',
    marginLeft: 'auto',  // pins the group to the right edge
  },
  lockedBanner: {
    background: '#fff8e1',
    border: '1px solid #e0c36a',
    borderRadius: '6px',
    padding: '8px 12px',
    marginBottom: '12px',
    fontSize: '0.85rem',
    color: '#7a5d00',
  },
  previousButton: (disabled) => ({
    padding: '12px 24px',
    fontSize: '1rem',
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: 'transparent',
    color: disabled ? '#bbb' : '#555',
    border: `1px solid ${disabled ? '#ddd' : '#999'}`,
    borderRadius: '6px',
    transition: 'border 0.15s, color 0.15s',
  }),
  skipButton: (disabled) => ({
    padding: '12px 24px',
    fontSize: '1rem',
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: 'transparent',
    color: disabled ? '#bbb' : '#555',
    border: `1px solid ${disabled ? '#ddd' : '#999'}`,
    borderRadius: '6px',
    transition: 'border 0.15s, color 0.15s',
  }),
  submitButton: (disabled) => ({
    padding: '12px 32px',
    fontSize: '1rem',
    fontWeight: 'bold',
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: disabled ? '#ccc' : '#333',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    opacity: disabled ? 0.6 : 1,
    transition: 'background 0.15s',
  }),
};

export default function QuestionCard({
  question,
  selectedAnswers,
  onSelectAnswer,
  onSubmit,
  onPrevious,
  isPreviousDisabled,
  onSkip,
  isSkipped,
  isSubmitted,
  eliminatedChoices = [],
  onToggleElimination,
  onReport,
  isFlagged = false,
  onFlag,
  mode = 'practice',
  onNext,
  // PMI-style exam breaks (Simulation only): once the learner passes a break
  // threshold, all earlier questions are locked — still viewable, but answers,
  // flags, and eliminations can no longer be changed.
  isLocked = false,
}) {
  // Interactive case types (enhanced_matching / ordered_sequence) store
  // object-shaped choices; the classic list below would crash on them, so
  // rendering is delegated to the question-type registry's LearnerRenderer.
  const interactiveDef = question && !Array.isArray(question.choices)
    ? getQuestionType(question.type)
    : null;
  const InteractiveRenderer = interactiveDef && interactiveDef.LearnerRenderer
    ? interactiveDef.LearnerRenderer
    : null;

  // ordered_sequence: the displayed order IS an answer — seed the draft with
  // the delivered order so submitting without reordering is a valid choice.
  useEffect(() => {
    if (question && question.type === 'ordered_sequence' && InteractiveRenderer
        && selectedAnswers.length === 0 && !isSubmitted && !isLocked) {
      onSelectAnswer(((question.choices && question.choices.items) || []).map((i) => String(i.id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question && question.id]);

  if (!question) return null;

  const { question: text, choices, multiSelect, correctAnswers, category } = question;
  const requiredCount = multiSelect ? correctAnswers.length : 1;
  const interactiveComplete = !InteractiveRenderer
    || (question.type === 'enhanced_matching'
      ? selectedAnswers.length === ((choices && choices.prompts) || []).length
      : selectedAnswers.length > 0);
  const isSubmitDisabled = InteractiveRenderer
    ? !interactiveComplete
    : (selectedAnswers.length === 0
      || (multiSelect && selectedAnswers.length !== requiredCount));

  function handleChoiceClick(choiceId) {
    if (isLocked) return; // locked section — answers are read-only
    if (multiSelect) {
      // Toggle in/out of selection
      if (selectedAnswers.includes(choiceId)) {
        onSelectAnswer(selectedAnswers.filter((id) => id !== choiceId));
      } else if (selectedAnswers.length < requiredCount) {
        onSelectAnswer([...selectedAnswers, choiceId]);
      }
    } else {
      // Single select — replace selection
      onSelectAnswer([choiceId]);
    }
  }

  // Whether the eliminate toggle is visible at all.
  // Hidden after submission to reduce visual noise on the results/explanation view.
  const showEliminateButtons = !isSubmitted && !isLocked && typeof onToggleElimination === 'function';

  // Report button is always visible when onReport is provided (before and
  // after submission) so SMEs reviewing explanations can still file a report.
  const showReportButton = typeof onReport === 'function';

  return (
    <div style={styles.card} className={css.card}>
      {isLocked && (
        <div data-testid="locked-question-banner" style={styles.lockedBanner}>
          🔒 This section is locked — you can review this question, but answers
          and flags can no longer be changed.
        </div>
      )}
      <div style={styles.meta} className={css.meta}>
        <span>{category || 'General'}</span>
        {multiSelect && (
          <span style={{ color: '#e07b00' }}>Select exactly {correctAnswers.length}</span>
        )}
      </div>

      {/* Previously-skipped indicator — shown when returning to a skipped question */}
      {isSkipped && (
        <div style={styles.skippedBadge}>↺ Previously skipped</div>
      )}

      {/* Phase 3 — visual_interpretation exhibit (migration 013).  Renders
          nothing when question.image is null/missing so non-visual question
          types are unaffected. */}
      <QuestionExhibit image={question.image} />

      <div style={styles.questionText} className={css.questionText}>{text}</div>

      {multiSelect && !InteractiveRenderer && (
        <div style={styles.multiSelectNote}>
          💡 Select exactly {requiredCount} answer{requiredCount !== 1 ? 's' : ''}.
          {' '}({selectedAnswers.length} of {requiredCount} selected)
        </div>
      )}

      {InteractiveRenderer ? (
        <div data-testid="interactive-answer-area" style={{ margin: '12px 0' }}>
          {question.type === 'enhanced_matching' && (
            <div style={styles.multiSelectNote}>
              💡 Match every item — {selectedAnswers.length} of {((choices && choices.prompts) || []).length} matched.
            </div>
          )}
          <InteractiveRenderer
            question={question}
            value={selectedAnswers}
            onChange={(next) => { if (!isSubmitted && !isLocked) onSelectAnswer(next); }}
            disabled={!!isSubmitted || isLocked}
          />
        </div>
      ) : (
        <ul style={styles.choicesList}>
          {choices.map((choice) => {
            const isSelected = selectedAnswers.includes(choice.id);
            const isEliminated = eliminatedChoices.includes(choice.id);
            return (
              <li key={choice.id} style={styles.choiceRow}>
                <button
                  data-testid="choice-button"
                  data-choice-id={choice.id}
                  data-eliminated={isEliminated ? 'true' : 'false'}
                  style={styles.choiceButton(isSelected, isEliminated)}
                  onClick={() => handleChoiceClick(choice.id)}
                >
                  <span style={styles.choiceId}>{choice.id}.</span>
                  {choice.text}
                </button>

                {showEliminateButtons && (
                  <button
                    data-testid={`eliminate-button-${choice.id}`}
                    aria-label={
                      isEliminated
                        ? `Restore choice ${choice.id}`
                        : `Eliminate choice ${choice.id}`
                    }
                    style={styles.eliminateButton(isEliminated)}
                    onClick={() => onToggleElimination(choice.id)}
                  >
                    ⊘
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div style={styles.submitRow} className={css.submitRow}>
        {/* ← Previous — only rendered when onPrevious is provided */}
        {onPrevious && (
          <button
            data-testid="previous-button"
            style={styles.previousButton(!!isPreviousDisabled)}
            disabled={!!isPreviousDisabled}
            onClick={onPrevious}
          >
            ← Previous
          </button>
        )}

        {/* 🚩 Flag — toggles review flag; visible when onFlag is provided */}
        {typeof onFlag === 'function' && (
          <button
            data-testid="flag-button"
            style={{
              ...styles.flagButton,
              ...(isFlagged ? styles.flagButtonActive : {}),
            }}
            onClick={isLocked ? undefined : onFlag}
            disabled={isLocked}
            aria-pressed={isFlagged}
            title={isFlagged ? 'Unflag this question' : 'Flag for review'}
          >
            🚩 {isFlagged ? 'Flagged' : 'Flag'}
          </button>
        )}

        {/* ⚑ Report — always visible when onReport is provided */}
        {showReportButton && (
          <button
            data-testid="report-button"
            style={styles.reportButton}
            onClick={onReport}
            aria-label="Report this question"
          >
            ⚑ Report
          </button>
        )}

        {/* Skip and Submit grouped to the right */}
        <div style={styles.rightActions} className={css.rightActions}>
          {/* Skip → — only rendered when onSkip is provided */}
          {onSkip && (
            <button
              data-testid="skip-button"
              style={styles.skipButton(!!isSubmitted || isLocked)}
              disabled={!!isSubmitted || isLocked}
              onClick={onSkip}
            >
              Skip →
            </button>
          )}

          {/* In simulation (full) mode the button morphs to "Next Question →"
              after the answer is recorded — no separate panel is shown. */}
          {mode === 'full' && isSubmitted ? (
            <button
              data-testid="next-question-button"
              style={styles.submitButton(false)}
              onClick={onNext}
            >
              Next Question →
            </button>
          ) : (
            <button
              data-testid="submit-button"
              style={styles.submitButton(isSubmitDisabled || isLocked)}
              disabled={isSubmitDisabled || isLocked}
              onClick={onSubmit}
            >
              Submit Answer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
